import { Document, Page, View, Text, Image, StyleSheet, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import logo from '../../../assets/images/logo.png';
import { InvoiceData } from '../InvoicePdfGenerator/InvoicePdfGenerator';

interface InvoicePdfProps {
  invoiceNumber: string;
  invoiceData: InvoiceData;
  close?: () => void;
}

const styles = StyleSheet.create({
  page: {
    fontSize: '11px',
    fontFamily: 'Helvetica',
    padding: '20px',
    color: '#333'
  },
  outer: {
    margin: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    borderBottom: '2px solid #29495e',
    paddingBottom: '15px',
    marginBottom: '20px',
    backgroundColor: '#f8f9fa'
  },
  headerRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingRight: '30px'
  },
  image: {
    height: 'auto',
    width: '240px',
    marginLeft: '30px',
    marginTop: '25px'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '20px'
  },
  halfCol: {
    width: '50%',
    padding: '0 15px'
  },
  title: {
    fontSize: '28px',
    marginBottom: '10px',
    textAlign: 'right',
    color: '#29495e',
    fontWeight: 'bold'
  },
  invoiceNumberText: {
    fontSize: '14px',
    color: '#4B5563'
  },
  label: {
    fontWeight: 'bold',
    marginRight: '5px',
    color: '#4B5563'
  },
  detailRow: {
    marginBottom: '6px'
  },
  section: {
    marginBottom: '20px',
    padding: '0 15px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '12px',
    color: '#29495e',
    paddingBottom: '4px',
    borderBottom: '1px solid #e2e8f0'
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginTop: '25px',
    marginLeft: '15px',
    marginRight: '15px',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    borderBottomStyle: 'solid'
  },
  tableHeader: {
    backgroundColor: '#EBF4FF',
    fontWeight: 'bold',
    color: '#29495e'
  },
  tableCell: {
    padding: '8px 10px',
    borderRightWidth: 1,
    borderRightColor: '#E2E8F0',
    borderRightStyle: 'solid',
    width: '25%',
    textAlign: 'left'
  },
  lastCell: {
    borderRightWidth: 0
  },
  total: {
    marginTop: '20px',
    marginRight: '15px',
    textAlign: 'right',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#29495e'
  },
  notes: {
    marginTop: '30px',
    marginLeft: '15px',
    marginRight: '15px',
    padding: '15px',
    backgroundColor: '#F7FAFC',
    borderRadius: '4px',
    borderLeft: '4px solid #29495e'
  },
  footerText: {
    textAlign: 'center',
    fontSize: '9px',
    marginTop: '30px',
    color: '#718096'
  },
  downloadButton: {
    backgroundColor: '#29495e',
    color: 'white',
    padding: '10px 15px',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'flex',
    margin: '0 auto'
  }
});

export const InvoiceDocument = ({ invoiceNumber, invoiceData }: InvoicePdfProps) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.outer}>
          <View style={styles.header}>
            <Image src={logo} style={styles.image} />
            <View style={styles.headerRight}>
              <Text style={styles.title}>INVOICE</Text>
              <Text style={styles.invoiceNumberText}>#{invoiceNumber}</Text>
            </View>
          </View>

          <View style={styles.section}>
            <View style={styles.row}>
              <View style={styles.halfCol}>
                <Text style={styles.sectionTitle}>Invoice Details</Text>
                <Text style={styles.detailRow}><Text style={styles.label}>Date:</Text> {invoiceData.issueDate}</Text>
                {invoiceData.companyName && (
                  <Text style={styles.detailRow}><Text style={styles.label}>Company:</Text> {invoiceData.companyName}</Text>
                )}
                {invoiceData.companyAddress && (
                  <Text style={styles.detailRow}><Text style={styles.label}>Address:</Text> {invoiceData.companyAddress}</Text>
                )}
                {invoiceData.vatNumber && (
                  <Text style={styles.detailRow}><Text style={styles.label}>VAT Number:</Text> {invoiceData.vatNumber}</Text>
                )}
                {invoiceData.contactNumber && (
                  <Text style={styles.detailRow}><Text style={styles.label}>Tel:</Text> {invoiceData.contactNumber}</Text>
                )}
                {invoiceData.email && (
                  <Text style={styles.detailRow}><Text style={styles.label}>Email:</Text> {invoiceData.email}</Text>
                )}
              </View>
              <View style={styles.halfCol}>
                <Text style={styles.sectionTitle}>Load Information</Text>
                <Text style={styles.detailRow}><Text style={styles.label}>Container #:</Text> {invoiceData.loadDetails.containerNumber}</Text>
                <Text style={styles.detailRow}><Text style={styles.label}>Client Ref:</Text> {invoiceData.loadDetails.loadNumber}</Text>
              </View>
            </View>

            <View style={styles.table}>
              <View style={[styles.tableRow, styles.tableHeader]}>
                <Text style={styles.tableCell}>Description</Text>
                <Text style={styles.tableCell}>Quantity</Text>
                <Text style={styles.tableCell}>Unit Price</Text>
                <Text style={[styles.tableCell, styles.lastCell]}>Amount</Text>
              </View>

              {invoiceData.items.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.description}</Text>
                  <Text style={styles.tableCell}>{item.quantity}</Text>
                  <Text style={styles.tableCell}>R {item.unitPrice.toFixed(2)}</Text>
                  <Text style={[styles.tableCell, styles.lastCell]}>R {item.amount.toFixed(2)}</Text>
                </View>
              ))}
            </View>

            <View style={styles.total}>
              <Text>Total Amount: R {invoiceData.totalAmount.toFixed(2)}</Text>
            </View>

            {invoiceData.notes && (
              <View style={styles.notes}>
                <Text style={styles.label}>Notes:</Text>
                <Text>{invoiceData.notes}</Text>
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

const InvoicePdf = (props: InvoicePdfProps) => (
  <div className="shadow-lg bg-white rounded-lg overflow-hidden">
    <div className="bg-blue-600 text-white p-3 text-center font-semibold">
      Invoice.{props.invoiceData.loadDetails.loadNumber}.pdf
    </div>

    <div style={{ height: '80vh' }}>
      <PDFViewer width="100%" height="100%" showToolbar={false}>
        <InvoiceDocument 
          invoiceNumber={props.invoiceNumber} 
          invoiceData={props.invoiceData}
        />
      </PDFViewer>
    </div>

    <div className="text-center p-4 bg-gray-50">
      <PDFDownloadLink
        document={<InvoiceDocument invoiceNumber={props.invoiceNumber} invoiceData={props.invoiceData} />} 
        fileName={`Invoice.${props.invoiceData.loadDetails.loadNumber}.pdf`}
        className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition duration-200"
      >
        {({ loading }) => (loading ? 'Preparing document...' : 'Download PDF')}
      </PDFDownloadLink>
    </div>
  </div>
);

export default InvoicePdf;